<template>
  <Teleport to="#top-menu-bar" defer>
    <PageTopBar>
      <template #item-name>
        <span>Search &amp; Import Terms from OLS</span>
      </template>
    </PageTopBar>
  </Teleport>

  <SearchShell
    :is-loading="isSearching"
    :has-searched="hasSearched"
    :result-count="results.length"
    :search-query="query"
    fill
  >
    <template #filters>
      <div class="columns mb-4">
        <div class="column is-6">
          <OlsSearchBar
            v-model="query"
            placeholder="Search for ontology terms (e.g. apoptosis, glucose)..."
            :loading="isSearching"
            @search="doSearch"
          />
        </div>
        <div class="column is-3">
          <div class="field">
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="ontologyFilter">
                  <option value="">All ontologies</option>
                  <!-- Searching an ontology is always allowed — only the
                       import actions are gated — so no option is disabled;
                       the label still flags import dead ends. -->
                  <option
                    v-for="ont in localOntologies"
                    :key="ont.id"
                    :value="ont.code"
                  >
                    {{ ontologyOptionLabel(ont) }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <label class="checkbox mt-2">
            <input v-model="exactMatch" type="checkbox" />
            Exact match
          </label>
        </div>
      </div>
    </template>

    <template #loading>
      <ProgressBar />
    </template>

    <template #initial>
      <div class="notification is-light has-text-centered">
        <span class="icon">
          <i class="fa fa-search" />
        </span>
        <span>
          Search the
          <a href="https://www.ebi.ac.uk/ols4/" target="_blank" rel="noopener">
            Ontology Lookup Service (OLS)
          </a>
          for terms to import into LabID.
        </span>
      </div>
    </template>

    <template #result-header>
      <span>
        Showing {{ results.length }} of {{ totalElements }} results
        <template v-if="query.trim()">for "{{ query.trim() }}"</template>
      </span>
      <DropdownButton
        :label="`${pageSize} / page`"
        size="small"
        fit-content
        align="end"
      >
        <button
          v-for="opt in PAGE_SIZE_OPTIONS"
          :key="opt"
          class="dd-item"
          :class="{
            'has-text-weight-semibold has-text-primary': opt === pageSize,
          }"
          @click="onPageSizeChange(opt)"
        >
          {{ opt }}
        </button>
      </DropdownButton>
    </template>

    <template #results>
      <div class="term-grid">
        <div class="term-grid__header" aria-hidden="true">
          <span>Ontology</span>
          <span class="tgh-term-id">ID</span>
          <span>Term</span>
          <span class="tgh-desc">Description</span>
          <span></span>
        </div>

        <div
          v-for="(term, i) in results"
          :key="`${term.term_iri}-${i}`"
          class="term-grid__row"
          :class="{ 'term-grid__row--obsolete': term.is_obsolete }"
          :data-term-iri="term.term_iri"
        >
          <div class="term-grid__cell term-grid__cell--ont">
            <div class="tags has-addons mb-0">
              <span class="tag is-light">{{ term.ontology_id }}</span>
              <!-- Importing an ontology is superuser-only on the server -->
              <span
                v-if="!findLocalOntology(term.ontology_id) && isSuperUser"
                v-tooltip="`Import ${term.ontology_id} ontology`"
                class="tag term-grid__dl-tag"
                @click="openOntologyImportModal(term)"
              >
                <span class="icon is-small">
                  <i class="fa fa-download" />
                </span>
              </span>
            </div>
          </div>

          <div class="term-grid__cell term-grid__cell--term-id">
            <!-- The ID links out to the public ontology page; importing is
                 the Import button's job alone -->
            <a
              v-if="term.term_iri"
              v-tooltip="`View ${term.term_id} in its ontology`"
              :href="toHttps(term.term_iri)"
              target="_blank"
              rel="noopener"
              class="tag is-light term-grid__term-id-link"
              @click.stop
            >
              <span class="icon is-small mr-1">
                <i class="fa-solid fa-arrow-up-right-from-square fa-2xs" />
              </span>
              <span class="term-grid__term-id-text">{{ term.term_id }}</span>
            </a>
            <span v-else class="tag is-light term-grid__term-id-link">
              <span class="term-grid__term-id-text">{{ term.term_id }}</span>
            </span>
          </div>

          <div class="term-grid__cell term-grid__cell--name">
            <span class="has-text-weight-semibold">{{ term.name }}</span>
            <span
              v-if="term.is_obsolete"
              class="tag is-warning is-light is-small ml-1"
            >
              obsolete
            </span>
            <span
              v-if="importedByIri[term.term_iri]"
              v-tooltip="importedTooltip(term)"
              class="tag is-success is-light is-small ml-1"
            >
              <span class="icon is-small">
                <i class="fa fa-check" />
              </span>
              <span>imported</span>
            </span>
          </div>

          <div class="term-grid__cell term-grid__cell--desc">
            <!-- Clamped to keep rows short; click toggles the full text -->
            <span
              v-if="term.description"
              class="term-grid__desc-text"
              :class="{
                'term-grid__desc-text--expanded':
                  expandedDescriptions[term.term_iri],
              }"
              @click="toggleDescription(term)"
            >
              {{ term.description }}
            </span>
          </div>

          <div class="term-grid__cell term-grid__cell--action">
            <BaseButton
              size="small"
              variant="primary"
              :outlined="true"
              :disabled="!canImportTerm(term)"
              :tooltip="
                canImportTerm(term)
                  ? undefined
                  : !canImport
                    ? 'You do not have permission to import terms'
                    : findLocalOntology(term.ontology_id)
                      ? 'No category allows term import for this ontology'
                      : 'Import the ontology first'
              "
              :icon="['fa', 'fa-download']"
              label="Import"
              @click="openImportModal(term)"
            />
          </div>
        </div>
      </div>
    </template>

    <template #pagination>
      <Pagination
        v-if="totalPages > 1"
        :current="currentPage + 1"
        :total="totalPages"
        @page-change="goToPage($event - 1)"
      />
    </template>
  </SearchShell>

  <!-- Ontology Import Modal -->
  <ModalCard
    v-if="pendingOntologyImport"
    :title="`Import ontology: ${pendingOntologyImport.ontologyId}`"
    :handle-close="() => (pendingOntologyImport = null)"
  >
    <template #body>
      <OlsOntologyImportForm
        :ontology-id="pendingOntologyImport.ontologyId"
        @imported="onOntologyImported"
      />
    </template>
  </ModalCard>

  <!-- Term Import Modal -->
  <ModalCard
    v-if="showImportModal"
    title="Import Term"
    :handle-close="closeImportModal"
  >
    <template #body>
      <div class="content">
        <p>
          Import term
          <strong>{{ selectedTerm?.name }}</strong>
          (
          <code>{{ selectedTerm?.term_id }}</code>
          ) from
          <span class="tag is-info is-light">
            {{ selectedTerm?.ontology_id }}
          </span>
          ?
        </p>

        <div
          v-if="selectedTermImported"
          class="notification is-success is-light is-size-7"
        >
          <span class="icon">
            <i class="fa fa-check" />
          </span>
          <span v-if="selectedTermLinkedNames.length">
            This term is already imported and linked to:
            <strong>{{ selectedTermLinkedNames.join(', ') }}</strong>
          </span>
          <span v-else>
            This term is already imported but not linked to a vocabulary term
            yet.
          </span>
        </div>

        <!-- Superuser: dual-mode radio toggle -->
        <template v-if="isSuperUser">
          <div class="field">
            <label class="radio">
              <input
                v-model="importMode"
                type="radio"
                name="importMode"
                value="link"
              />
              Link to existing vocabulary term
            </label>
          </div>
          <div v-if="importMode === 'link'" class="field ml-5 mb-4">
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="localTermId">
                  <option value="">— Select a term —</option>
                  <option v-for="t in localTerms" :key="t.id" :value="t.id">
                    {{ localTermOptionLabel(t) }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="radio">
              <input
                v-model="importMode"
                type="radio"
                name="importMode"
                value="category"
              />
              Create new term in category
            </label>
          </div>
          <div v-if="importMode === 'category'" class="field ml-5 mb-4">
            <div class="select is-fullwidth">
              <select v-model="selectedCategoryId">
                <option value="">— Select a category —</option>
                <option
                  v-for="opt in importableCategories"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ categoryOptionLabel(opt) }}
                </option>
              </select>
            </div>
            <p
              v-if="importableCategories.length === 0"
              class="help has-text-grey"
            >
              No categories allow term import for this ontology.
            </p>
          </div>
        </template>

        <!-- Non-superuser: category mode only -->
        <template v-else>
          <div v-if="importableCategories.length > 0" class="field">
            <label class="label">Create new term in category</label>
            <div class="select is-fullwidth">
              <select v-model="selectedCategoryId">
                <option value="">— Select a category —</option>
                <option
                  v-for="opt in importableCategories"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ categoryOptionLabel(opt) }}
                </option>
              </select>
            </div>
          </div>
          <div v-else class="notification is-info is-light">
            <span class="icon">
              <i class="fa fa-info-circle" />
            </span>
            <span>
              No categories allow term import for this ontology. Contact your
              admin.
            </span>
          </div>
        </template>
      </div>
    </template>
    <template #footer>
      <BaseButton
        variant="primary"
        :loading="isImporting"
        :disabled="isImporting || !isImportReady"
        :icon="['fa', 'fa-download']"
        label="Import"
        @click="importTerm"
      />
      <BaseButton :outlined="true" label="Cancel" @click="closeImportModal" />
    </template>
  </ModalCard>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { get } from '@/api'
import { importOntologyTerm, searchOlsTerms } from '@/api/ols'
import { hasPrivilege } from '@/auth/authorization'
import { usePageShell } from '@/composables/usePageShell'
import { useStore } from '@/store'
import { getErrorMessage, toHttps, truncateText } from '@/utils'

import OlsOntologyImportForm from '@/components/OlsOntologyImportForm.vue'
import OlsSearchBar from '@/components/OlsSearchBar.vue'
import PageTopBar from '@/components/PageTopBar.vue'
import SearchShell from '@/components/SearchShell.vue'

export default {
  name: 'OlsTermSearchView',
  components: {
    OlsOntologyImportForm,
    OlsSearchBar,
    PageTopBar,
    SearchShell,
  },
  setup() {
    usePageShell({ title: 'Search & Import Terms from OLS' })
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    // Search state
    const query = ref('')
    const ontologyFilter = ref('')
    const exactMatch = ref(false)
    const results = ref([])
    const isSearching = ref(false)
    const hasSearched = ref(false)
    const PAGE_SIZE_OPTIONS = [15, 30, 60, 100]
    const totalElements = ref(0)
    const currentPage = ref(0)
    const pageSize = ref(PAGE_SIZE_OPTIONS[0])

    // Local data
    const localOntologies = ref([])
    const localTerms = ref([])
    // term_iri → local OntologyTerm record (with linked_terms) for the
    // current result page — marks results that are already imported
    const importedByIri = ref({})
    // term_iri → true while its clamped description is expanded
    const expandedDescriptions = ref({})

    function toggleDescription(term) {
      expandedDescriptions.value = {
        ...expandedDescriptions.value,
        [term.term_iri]: !expandedDescriptions.value[term.term_iri],
      }
    }

    // Auth state. Linking an ontology term to an existing vocabulary term is
    // superuser-only on the server, so the dual-mode import follows that flag;
    // the `add` privilege only decides whether the create-a-term mode is on.
    const isSuperUser = computed(
      () => !!store.getters['auth/loggedInUser']?.isSuperUser,
    )
    const canAddOntologyTerm = computed(() =>
      hasPrivilege('vocabularies', 'ontologyterm', 'add'),
    )

    // Ontology import modal state
    const pendingOntologyImport = ref(null) // { ontologyId, targetTermIri }

    function openOntologyImportModal(term) {
      pendingOntologyImport.value = {
        ontologyId: term.ontology_id,
        targetTermIri: term.term_iri,
      }
    }

    async function onOntologyImported() {
      const targetIri = pendingOntologyImport.value?.targetTermIri
      pendingOntologyImport.value = null
      // The import may have created a new category (or linked an existing
      // one), so the category cache must refresh too or the term-import
      // modal offers no category for the fresh ontology.
      await Promise.all([loadLocalOntologies(), loadCategories()])
      if (hasSearched.value) {
        await doSearch()
        if (targetIri) blinkTerm(targetIri)
      }
    }

    async function blinkTerm(termIri) {
      await nextTick()
      // IRIs contain ':' and '/', so escape before using as a CSS selector
      const el = document.querySelector(
        `[data-term-iri="${CSS.escape(termIri)}"]`,
      )
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('term-grid__row--blink')
      setTimeout(() => el.classList.remove('term-grid__row--blink'), 1400)
    }

    // Import modal state
    const showImportModal = ref(false)
    const selectedTerm = ref(null)
    const targetOntologyId = ref('')
    const localTermId = ref('')
    const importMode = ref('')
    const selectedCategoryId = ref('')
    const allCategories = ref([])
    const categoriesLoaded = ref(false)
    const isImporting = ref(false)

    const totalPages = computed(() =>
      Math.ceil(totalElements.value / pageSize.value),
    )

    function findLocalOntology(olsOntologyId) {
      return localOntologies.value.find(
        (o) => o.code && o.code.toLowerCase() === olsOntologyId?.toLowerCase(),
      )
    }

    async function doSearch() {
      const q = query.value?.trim()
      if (!q) return

      isSearching.value = true
      hasSearched.value = true
      try {
        const params = {
          q,
          page: currentPage.value,
          size: pageSize.value,
        }
        if (ontologyFilter.value) {
          params.code = ontologyFilter.value
        }
        if (exactMatch.value) {
          params.exact = true
        }
        const { terms, totalElements: total } = await searchOlsTerms(params)
        results.value = terms
        totalElements.value = total
        loadImportedStatus(terms)
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message: getErrorMessage(error, 'Failed to search OLS terms'),
        })
        results.value = []
      } finally {
        isSearching.value = false
        // Sync search state to URL after request completes. Keep `category`
        // (import-modal pre-selection passed in by entry points).
        const urlQuery = { q }
        if (ontologyFilter.value) urlQuery.ontology = ontologyFilter.value
        if (exactMatch.value) urlQuery.exact = '1'
        if (currentPage.value > 0) urlQuery.page = currentPage.value + 1
        if (route.query.category) urlQuery.category = route.query.category
        router.replace({ query: urlQuery }).catch(() => {})
      }
    }

    watch([ontologyFilter, exactMatch], () => {
      if (hasSearched.value && query.value?.trim()) {
        currentPage.value = 0
        doSearch()
      }
    })

    function onPageSizeChange(size) {
      if (size != null) pageSize.value = size
      if (hasSearched.value && query.value?.trim()) {
        currentPage.value = 0
        doSearch()
      }
    }

    function goToPage(page) {
      if (page >= 0 && page < totalPages.value) {
        currentPage.value = page
        doSearch()
      }
    }

    // Extract plain value from API field objects ({ value: "x" } → "x")
    // Record references ({ value: { id, name, model_name } }) unwrap to the name string
    function unwrapField(field) {
      if (field && typeof field === 'object' && 'value' in field) {
        const val = field.value
        if (val && typeof val === 'object' && 'model_name' in val) {
          return val.name
        }
        return val
      }
      return field
    }

    function normalizeRecord(record) {
      const result = {}
      for (const key in record) {
        result[key] = unwrapField(record[key])
      }
      return result
    }

    async function loadLocalOntologies() {
      try {
        const { status, data } = await get('vocabularies/ontologies/', {
          params: { format: 'json' },
        })
        if (status === 200) {
          const raw = data.results || data || []
          localOntologies.value = raw.map(normalizeRecord)
        }
      } catch {
        // silent — filter stays empty
      }
    }

    // Candidates for "link to existing vocabulary term": only terms whose
    // category is linked to the target ontology (server: ontology_id filter
    // on category__ontologies) — not a page of arbitrary terms.
    async function loadLocalTerms(ontologyId) {
      localTerms.value = []
      if (!ontologyId) return
      try {
        const { status, data } = await get('vocabularies/terms/', {
          params: {
            ontology_id: ontologyId,
            response_format: 'flat',
            fields: 'id,name,category',
            page_size: 'max',
          },
        })
        if (status === 200) {
          localTerms.value = data.results || data || []
        }
      } catch {
        // silent — local terms list stays empty
      }
    }

    // Which of the shown OLS results already exist locally, and what they
    // link to. Not awaited by the search — badges pop in when it resolves.
    async function loadImportedStatus(terms) {
      importedByIri.value = {}
      const iris = terms.map((t) => t.term_iri).filter(Boolean)
      if (!iris.length) return
      try {
        const { status, data } = await get('vocabularies/ontologyterms/', {
          params: {
            term_iri_list: iris.join(','),
            deleted: false,
            response_format: 'flat',
            fields: 'id,term_iri,linked_terms',
            page_size: 'max',
          },
        })
        if (status !== 200) return
        const map = {}
        for (const rec of data.results || []) map[rec.term_iri] = rec

        // The linked_terms record refs carry no category, so resolve the
        // linked vocabulary terms in one batch to say where each one lives.
        const linkedIds = [
          ...new Set(
            Object.values(map).flatMap((rec) =>
              (rec.linked_terms || []).map((t) => t.id),
            ),
          ),
        ]
        if (linkedIds.length) {
          const { status: termsStatus, data: termsData } = await get(
            'vocabularies/terms/',
            {
              params: {
                id_list: linkedIds.join(','),
                response_format: 'flat',
                fields: 'id,name,category',
                page_size: 'max',
              },
            },
          )
          if (termsStatus === 200) {
            const termById = {}
            for (const t of termsData.results || []) termById[t.id] = t
            for (const rec of Object.values(map)) {
              rec.linked_terms = (rec.linked_terms || []).map(
                (t) => termById[t.id] || t,
              )
            }
          }
        }
        importedByIri.value = map
      } catch {
        // silent — results just miss the imported badge
      }
    }

    // "cocaine dependence (Ontology Mondo)" — linked term with its category
    function linkedTermDisplay(term) {
      const category = term.category?.label || term.category?.name
      return category ? `${term.name} (${category})` : term.name
    }

    function importedTooltip(term) {
      const record = importedByIri.value[term.term_iri]
      if (!record) return ''
      const names = (record.linked_terms || []).map(linkedTermDisplay)
      return names.length
        ? `Already imported — linked to: ${names.join(', ')}`
        : 'Already imported — not linked to a vocabulary term yet'
    }

    // "term name (Category Label)" — several categories can feed the same
    // ontology, so the option must say where each candidate term lives.
    function localTermOptionLabel(term) {
      const category = term.category?.label || term.category?.name
      return category ? `${term.name} (${category})` : term.name
    }

    const selectedTermImported = computed(
      () => importedByIri.value[selectedTerm.value?.term_iri] || null,
    )
    const selectedTermLinkedNames = computed(() =>
      (selectedTermImported.value?.linked_terms || []).map(linkedTermDisplay),
    )

    // Category option, flagging categories that already hold a vocabulary
    // term linked to the selected ontology term — creating another one
    // there would duplicate it.
    function categoryOptionLabel(cat) {
      const alreadyLinked = (selectedTermImported.value?.linked_terms || [])
        .filter((t) => t.category && String(t.category.id) === String(cat.id))
        .map((t) => t.name)
      return alreadyLinked.length
        ? `${cat.name} — already linked: ${alreadyLinked.join(', ')}`
        : cat.name
    }

    const isImportReady = computed(() => {
      if (!targetOntologyId.value) return false
      if (importMode.value === 'link') return !!localTermId.value
      if (importMode.value === 'category') return !!selectedCategoryId.value
      return true
    })

    async function loadCategories() {
      try {
        const { status, data } = await get(
          'vocabularies/categories/?response_format=flat&page_size=max',
        )
        if (status === 200) {
          const raw = data.results || data || []
          allCategories.value = raw.map(normalizeRecord)
          categoriesLoaded.value = true
        }
      } catch {
        allCategories.value = []
      }
    }

    function categoriesForOntology(ontologyId, olsOntologyCode) {
      return allCategories.value.filter((cat) => {
        // The allow_user_term_import flag gates regular users only —
        // superusers may import into any category linked to the ontology.
        if (!isSuperUser.value && !cat.allow_user_term_import) return false
        const ontologies = cat.ontologies || []
        return ontologies.some(
          (o) =>
            String(o.id || o) === String(ontologyId) ||
            (o.code && o.code.toLowerCase() === olsOntologyCode?.toLowerCase()),
        )
      })
    }

    const importableCategories = computed(() =>
      categoriesForOntology(
        targetOntologyId.value,
        selectedTerm.value?.ontology_id,
      ),
    )

    // A regular user can only turn an OLS hit into a term through a category
    // that accepts user term imports, so ontologies without one are dead ends.
    function canImportIntoOntology(ontology) {
      if (!ontology) return false
      if (isSuperUser.value) return true
      return categoriesForOntology(ontology.id, ontology.code).length > 0
    }

    function canImportTerm(term) {
      return canImportIntoOntology(findLocalOntology(term.ontology_id))
    }

    function ontologyOptionLabel(ontology) {
      const name = ontology.code
        ? `${ontology.name || ontology.code} (${ontology.code})`
        : ontology.name
      return canImportIntoOntology(ontology)
        ? name
        : `${name} — no import category`
    }

    // Mirrors the server rule on `import-term`: superusers, holders of the add
    // privilege, and users with at least one category open for term import.
    // Searching stays open to everyone — only the import actions are gated.
    const canImport = computed(
      () =>
        isSuperUser.value ||
        canAddOntologyTerm.value ||
        allCategories.value.some(
          (cat) =>
            cat.allow_user_term_import && (cat.ontologies || []).length > 0,
        ),
    )

    function openImportModal(term) {
      selectedTerm.value = term
      localTermId.value = ''
      selectedCategoryId.value = ''

      // Auto-select target ontology if there's a local match
      const localOnt = findLocalOntology(term.ontology_id)
      targetOntologyId.value = localOnt ? localOnt.id : ''

      // Set default import mode based on permissions
      importMode.value = isSuperUser.value ? 'link' : 'category'

      // Pre-select the category the user came from (?category=<uuid>), or
      // the only possible target category.
      const categories = importableCategories.value
      const preferred = categories.find((c) => c.id === route.query.category)
      if (preferred) {
        selectedCategoryId.value = preferred.id
      } else if (categories.length === 1) {
        selectedCategoryId.value = categories[0].id
      }

      showImportModal.value = true
      if (isSuperUser.value) {
        loadLocalTerms(targetOntologyId.value)
      }
    }

    function closeImportModal() {
      showImportModal.value = false
      selectedTerm.value = null
    }

    async function importTerm() {
      if (!selectedTerm.value || !targetOntologyId.value) return
      isImporting.value = true
      try {
        const payload = {
          ontology_id: targetOntologyId.value,
          term_iri: selectedTerm.value.term_iri,
        }
        if (importMode.value === 'link' && localTermId.value) {
          payload.local_term_id = localTermId.value
        } else if (
          importMode.value === 'category' &&
          selectedCategoryId.value
        ) {
          payload.category_id = selectedCategoryId.value
        }

        const { status } = await importOntologyTerm(payload)
        if (status === 200 || status === 201) {
          store.commit('notify', {
            type: 'success',
            message: `Term "${selectedTerm.value.name}" imported successfully`,
          })
          closeImportModal()
          loadImportedStatus(results.value)
        }
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message: getErrorMessage(error, 'Failed to import term from OLS'),
        })
      } finally {
        isImporting.value = false
      }
    }

    onMounted(async () => {
      loadLocalOntologies()
      await loadCategories()

      // Only warn on a categories response we actually got — a failed request
      // must not mislabel a legitimate user. Searching stays available either
      // way; the import actions below are disabled through canImportTerm.
      if (categoriesLoaded.value && !canImport.value) {
        store.commit('notify', {
          type: 'warning',
          message:
            'You do not have permission to import terms from an ontology. ' +
            'You can search OLS, but the import actions are disabled.',
        })
      }

      // Restore search state from URL query params. `ontology` also works
      // without `q` — entry points like the category page link here with a
      // pre-set ontology filter before any search ran.
      if (route.query.ontology) ontologyFilter.value = route.query.ontology
      if (route.query.q) {
        query.value = route.query.q
        if (route.query.exact) exactMatch.value = true
        if (route.query.page > 1)
          currentPage.value = Number(route.query.page) - 1
        doSearch()
      }
    })

    return {
      query,
      ontologyFilter,
      exactMatch,
      results,
      isSearching,
      hasSearched,
      totalElements,
      totalPages,
      currentPage,
      localOntologies,
      localTerms,
      canAddOntologyTerm,
      isSuperUser,
      canImport,
      canImportIntoOntology,
      canImportTerm,
      ontologyOptionLabel,
      pendingOntologyImport,
      openOntologyImportModal,
      onOntologyImported,
      showImportModal,
      selectedTerm,
      targetOntologyId,
      localTermId,
      importMode,
      selectedCategoryId,
      importableCategories,
      isImporting,
      isImportReady,
      toHttps,
      truncateText,
      findLocalOntology,
      expandedDescriptions,
      toggleDescription,
      localTermOptionLabel,
      importedByIri,
      importedTooltip,
      categoryOptionLabel,
      selectedTermImported,
      selectedTermLinkedNames,
      PAGE_SIZE_OPTIONS,
      pageSize,
      doSearch,
      goToPage,
      onPageSizeChange,
      openImportModal,
      closeImportModal,
      importTerm,
    }
  },
}
</script>

<style lang="scss" scoped>
.term-grid {
  container-type: inline-size;
  container-name: term-results;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.term-grid__header {
  display: none;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-light);
  padding: var(--space-1) var(--space-3);
}

// ── Card layout (narrow) ────────────────────────────────────
.term-grid__row {
  display: grid;
  grid-template-areas:
    'ont     action'
    'term-id term-id'
    'name    name'
    'desc    desc';
  grid-template-columns: 1fr auto;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-3);
  background: var(--bg-default);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  &:hover {
    background: var(--bg-subtle);
    border-color: var(--border-default);
  }

  &--obsolete {
    opacity: 0.7;
  }
}

.term-grid__cell {
  display: flex;
  align-items: center;
  min-width: 0;

  &--ont {
    grid-area: ont;
  }
  &--term-id {
    grid-area: term-id;
  }
  &--name {
    grid-area: name;
    flex-wrap: wrap;
    gap: var(--space-1);
    font-size: 0.9rem;
  }
  &--desc {
    grid-area: desc;
  }
  &--action {
    grid-area: action;
    justify-content: flex-end;
    align-self: center;
  }
}

// ── Ontology addon — import shortcut ────────────────────────
// Reads as an actionable button (primary outline, hover fill) — the earlier
// muted grey styling looked disabled even though it was clickable.
.term-grid__dl-tag {
  background: transparent;
  border: 1px solid var(--primary);
  color: var(--primary);
  cursor: pointer;

  &:hover {
    background: var(--primary);
    color: #fff;
  }
}

// ── Term ID → external ontology page ─────────────────────────
.term-grid__term-id-link {
  gap: var(--space-1);
  max-width: 100%;
  text-decoration: none;

  &:hover {
    filter: brightness(0.92);
  }
}

.term-grid__term-id-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.term-grid__desc-text {
  font-size: 0.8rem;
  color: var(--text-light);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
}

// Click-expanded description: drop the clamp (double class outranks the
// container-query clamp overrides below)
.term-grid__desc-text.term-grid__desc-text--expanded {
  -webkit-line-clamp: unset;
}

// ── Medium: ont+term-id left, name+desc right ────────────────
@include cq(term-results, $cq-sm) {
  .tgh-term-id,
  .tgh-desc {
    display: none;
  }

  .term-grid__header {
    display: grid;
    grid-template-columns: 9rem 1fr 7rem;
    gap: var(--space-3);
  }

  .term-grid__row {
    grid-template-areas:
      'ont     name   action'
      'term-id desc   action';
    grid-template-columns: 9rem 1fr 7rem;
    align-items: start;
    gap: var(--space-2) var(--space-3);
  }

  .term-grid__cell--action {
    grid-row: 1 / 3;
    align-self: center;
  }
}

// ── Wide: all 5 columns on one row ───────────────────────────
@include cq(term-results, $cq-lg) {
  .tgh-term-id,
  .tgh-desc {
    display: revert;
  }

  .term-grid__header {
    display: grid;
    grid-template-columns: 6rem 9rem 1.2fr 2fr 8rem;
    gap: var(--space-3);
  }

  .term-grid__row {
    grid-template-areas: 'ont term-id name desc action';
    grid-template-columns: 6rem 9rem 1.2fr 2fr 8rem;
    align-items: center;
    gap: var(--space-3);
  }

  .term-grid__cell--action {
    grid-row: auto;
    align-self: center;
  }

  .term-grid__desc-text {
    -webkit-line-clamp: 2;
  }
}

@keyframes row-blink {
  0%,
  100% {
    background: transparent;
  }
  30%,
  70% {
    background: hsl(var(--bulma-info-h), var(--bulma-info-s), 92%);
  }
}

.term-grid__row--blink {
  animation: row-blink 1.4s ease;
}
</style>
